import React, { useEffect, useState } from 'react';

import './DragAndDrop.css';
import {
  buildFileUploadData,
  FileUploadData,
  hasDraggedFiles,
} from '../utils/file-upload';
import { useNotifications } from '../components/notifications/NotificationProvider';
import {
  showFileUploadErrorNotification,
  showUnmatchedEventFilesNotificationIfNeeded,
} from './upload-notifications';

type OwnProps = {
  onUpload: (data: FileUploadData) => void | Promise<void>;
};

export const DragAndDrop: React.FC<OwnProps> = ({ onUpload }) => {
  const [isDragShown, setIsDragShown] = useState(false);
  const notifications = useNotifications();

  useEffect(() => {
    const onDragEnter = (e: DragEvent) => {
      if (!hasDraggedFiles(e)) return;
      e.preventDefault();
      setIsDragShown(true);
    };

    const onDragLeave = (e: DragEvent) => {
      if (!hasDraggedFiles(e)) return;
      e.preventDefault();

      if (e.relatedTarget === null) {
        setIsDragShown(false);
      }
    };

    const onDragOver = (e: DragEvent) => {
      if (!hasDraggedFiles(e)) return;
      e.preventDefault();
    };

    const onDrop = async (e: DragEvent) => {
      if (!hasDraggedFiles(e)) return;

      e.preventDefault();
      setIsDragShown(false);

      const files = Array.from(e.dataTransfer?.files ?? []);
      if (files.length === 0) return;

      try {
        const data = await buildFileUploadData(files);
        showUnmatchedEventFilesNotificationIfNeeded(notifications, data);
        await onUpload(data);
      } catch (error) {
        showFileUploadErrorNotification(notifications, error);
      }
    };

    window.addEventListener('dragenter', onDragEnter);
    window.addEventListener('dragleave', onDragLeave);
    window.addEventListener('dragover', onDragOver);
    window.addEventListener('drop', onDrop);

    return () => {
      window.removeEventListener('dragenter', onDragEnter);
      window.removeEventListener('dragleave', onDragLeave);
      window.removeEventListener('dragover', onDragOver);
      window.removeEventListener('drop', onDrop);
    };
  }, [notifications, onUpload]);

  if (!isDragShown) return null;

  return (
    <div className='drop-overlay'>
      <div className='drop-overlay__zone'>
        <span className='drop-overlay__text'>
          Drop file to upload and analyze...
        </span>
      </div>
    </div>
  );
};
